import { create } from 'zustand';

export const useVoiceStore = create((set, get) => ({
  channelId:       null,
  channelName:     null,
  isMuted:         false,
  isDeafened:      false,
  isCameraOn:      false,
  isSharingScreen: false,
  localStream:     null,
  screenStream:    null,
  participants:    [],
  joinedAt:        null,   // timestamp when user joined
  incomingCall:    null,   // { callerId, callerName, callerAvatar, channelId, channelName }

  join: (channelId, channelName, user) => {
    const me = {
      id:         user?.id || 'local',
      name:       user?.display_name || user?.username || 'You',
      avatar:     user?.avatar,
      isMuted:    false,
      isCameraOn: false,
      isSpeaking: false,
      isLocal:    true,
    };
    set({ channelId, channelName, participants: [me], isMuted: false, isCameraOn: false, isSharingScreen: false, joinedAt: Date.now(), incomingCall: null });
  },

  receiveCall: (data) => set({ incomingCall: data }),
  declineCall: ()     => set({ incomingCall: null }),
  acceptCall:  ()     => {
    const { incomingCall } = get();
    if (!incomingCall) return;
    get().join(incomingCall.channelId, incomingCall.channelName, null);
  },

  leave: () => {
    const { localStream, screenStream } = get();
    localStream?.getTracks().forEach(t => t.stop());
    screenStream?.getTracks().forEach(t => t.stop());
    set({ channelId: null, channelName: null, participants: [], localStream: null, screenStream: null, isCameraOn: false, isSharingScreen: false, isMuted: false, isDeafened: false });
  },

  toggleMute: () => {
    const { localStream, isMuted } = get();
    localStream?.getAudioTracks().forEach(t => { t.enabled = isMuted; }); // isMuted means currently muted → enable
    set(s => ({ isMuted: !s.isMuted }));
  },

  toggleDeafen: () => set(s => ({ isDeafened: !s.isDeafened, isMuted: !s.isDeafened ? true : s.isMuted })),

  toggleCamera: async () => {
    const { isCameraOn, localStream } = get();
    if (isCameraOn) {
      localStream?.getVideoTracks().forEach(t => t.stop());
      const audioOnly = localStream?.getAudioTracks() || [];
      const newStream = audioOnly.length ? new MediaStream(audioOnly) : null;
      set({ isCameraOn: false, localStream: newStream });
      get().updateParticipant('local', { isCameraOn: false });
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 }, audio: false });
        // Merge with existing audio if present
        const existing = get().localStream;
        const tracks = [...(existing?.getAudioTracks() || []), ...stream.getVideoTracks()];
        const merged = new MediaStream(tracks);
        set({ isCameraOn: true, localStream: merged });
        get().updateParticipant('local', { isCameraOn: true, stream: merged });
      } catch (err) {
        console.error('Camera denied:', err);
      }
    }
  },

  toggleScreenShare: async () => {
    const { isSharingScreen, screenStream } = get();
    if (isSharingScreen) {
      screenStream?.getTracks().forEach(t => t.stop());
      set({ isSharingScreen: false, screenStream: null });
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        stream.getVideoTracks()[0].addEventListener('ended', () => {
          set({ isSharingScreen: false, screenStream: null });
        });
        set({ isSharingScreen: true, screenStream: stream });
      } catch (err) {
        console.error('Screen share denied:', err);
      }
    }
  },

  startMic: async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      set(s => ({
        localStream: s.localStream ? new MediaStream([...s.localStream.getTracks(), ...stream.getTracks()]) : stream,
      }));
    } catch (err) {
      console.error('Mic denied:', err);
    }
  },

  addParticipant: (p) => set(s => ({
    participants: s.participants.find(x => x.id === p.id) ? s.participants : [...s.participants, p],
  })),

  removeParticipant: (id) => set(s => ({ participants: s.participants.filter(p => p.id !== id) })),

  updateParticipant: (id, updates) => set(s => ({
    participants: s.participants.map(p => p.id === id ? { ...p, ...updates } : p),
  })),
}));
